<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :width="size"
    :height="size"
    aria-hidden="true"
    v-html="paths[name] || ''"
  />
</template>

<script setup>
/**
 * Thin stroked icons, in the Lucide style.
 *
 * The app's FontAwesome set is solid — filled glyphs that read as heavy next
 * to this interface's light borders and whitespace. These are drawn as
 * outlines at a hairline weight instead, inlined rather than pulled from a
 * package so the nav does not grow a dependency for six shapes.
 *
 * Everything scales from the 24x24 box, so the stroke stays even at any size.
 */
defineProps({
  name: { type: String, required: true },
  size: { type: [String, Number], default: 20 },
  strokeWidth: { type: [String, Number], default: 1.6 },
});

/*
 * Drawn for the size they are used at.
 *
 * The three in the navigation were Lucide's full-detail versions: a page
 * with three lines of text in it, a building with eight windows, two coins
 * overlapping with a figure on each. At 20px those details are a pixel wide
 * and fill in — the building in particular read as a grey smudge.
 *
 * Each is down to a handful of large shapes: a page with a folded corner and
 * two lines, a block with a door standing on the ground, a note with a coin
 * in the middle of it.
 */
const paths = {
  /* Scans — a document with two lines on it.
   *
   * The torn-off bill was tried at four teeth and at two; both leave a
   * ragged edge that at 20px reads as noise rather than as paper. Three
   * strokes, all of them straight or square: the sheet and the two figures
   * on it. */
  'file-text': `
    <rect width="12" height="18" x="6" y="3" rx="2"/>
    <path d="M9.5 9h5"/><path d="M9.5 13h5"/>`,

  /* Companies — a case with a handle.
   *
   * Buildings were tried twice and neither held at 20px: one block read as a
   * tent, two read as a bar chart. A case is two shapes, has an outline
   * nothing else in the set shares, and says "a business" rather than "a
   * house". */
  building: `
    <rect width="20" height="13" x="2" y="7" rx="2"/>
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>`,

  /* The rates — a line going up, with the arrow at its head.
   *
   * A rate is a number that moves, and that is what this draws. Coins and
   * notes said "money", which is also what the subscription and the payment
   * marks say; this one is about the movement, and no other icon in the set
   * has this shape. */
  coins: `
    <path d="M3 17l5.5-5.5 4 4L20 8"/>
    <path d="M14.5 8H20v5.5"/>`,

  user: `
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>`,

  // Back to where this was opened from.
  'arrow-left': `
    <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>`,

  // Swapping the company being worked in.
  'arrow-left-right': `
    <path d="m16 3 4 4-4 4"/><path d="M20 7H4"/>
    <path d="m8 21-4-4 4-4"/><path d="M4 17h16"/>`,

  // The three actions on a scan row.
  pencil: `
    <path d="M21.2 6.8a1 1 0 0 0-4-4L3.8 16.2a2 2 0 0 0-.5.8l-1.3 4.4a.5.5 0 0 0 .6.6l4.4-1.3a2 2 0 0 0 .8-.5z"/>
    <path d="m15 5 4 4"/>`,

  download: `
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <path d="m7 10 5 5 5-5"/><path d="M12 15V3"/>`,

  // The bin: the tapered basket, closed at its rim, and the lid as a line
  // held a few pixels clear above it. The gap is what makes it read as a lid
  // rather than a second rim. Its handle and the stripes down the inside are
  // left out — four more lines in a 14px square ran together into a smudge.
  trash: `
    <path d="M4.5 3h15"/>
    <path d="M5 7h14l-1.2 12.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8z"/>`,

  'credit-card': `
    <rect width="20" height="14" x="2" y="5" rx="2"/>
    <path d="M2 10h20"/>`,

  'log-out': `
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <path d="m16 17 5-5-5-5"/>
    <path d="M21 12H9"/>`,

  // The translate mark: a letter crossed by a stroke of another script. It
  // says "language" outright, where a globe only says "somewhere else".
  languages: `
    <path d="M2 5h12"/><path d="M7 2h1"/>
    <path d="m4 14 6-6 2-3"/><path d="m5 8 6 6"/>
    <path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>`,

  // Kept as the alternative: a globe drawn with continents rather than
  // meridians, which is what made the first one look like a wireframe ball.
  earth: `
    <circle cx="12" cy="12" r="10"/>
    <path d="M21.5 15H17a2 2 0 0 0-2 2v4.5"/>
    <path d="M7 3.3V5a3 3 0 0 0 3 3 2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.2"/>
    <path d="M11 22v-4a2 2 0 0 0-2-2 2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2"/>`,

  // An empty list: a tray with nothing in it.
  inbox: `
    <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
    <path d="M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.8 1.1z"/>`,

  camera: `
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z"/>
    <circle cx="12" cy="13" r="3"/>`,

  upload: `
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <path d="m17 8-5-5-5 5"/><path d="M12 3v12"/>`,

  // Uploading a whole register — a page of rows.
  table: `
    <rect width="18" height="18" x="3" y="3" rx="2"/>
    <path d="M3 9h18"/><path d="M3 15h18"/><path d="M12 9v12"/>`,

  filter: `
    <path d="M3 4h18l-7 8.5V20l-4-2v-5.5z"/>`,

  minus: `
    <path d="M5 12h14"/>`,

  plus: `
    <path d="M12 5v14"/><path d="M5 12h14"/>`,

  // Archiving: a box with its lid as a band across the top.
  archive: `
    <rect width="18" height="5" x="3" y="3" rx="1"/>
    <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8"/>
    <path d="M10 12h4"/>`,

  /* Settings as two sliders, not a gear.
   *
   * A gear's teeth are a dozen notches around a small circle; at the 15px
   * this is drawn at they close into a blob with a hole in it. Two rails
   * with a handle on each keeps four shapes at that size, and it says the
   * same thing — something here can be set. */
  settings: `
    <path d="M20 7h-9"/><path d="M14 17H5"/>
    <circle cx="7" cy="7" r="2.5"/><circle cx="17" cy="17" r="2.5"/>`,

  menu: `
    <path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/>`,

  // Sorting a column. The pair means "either way from here"; one alone means
  // the column is sorted and says which way.
  'chevrons-up-down': `
    <path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/>`,

  'chevron-up': `
    <path d="m18 15-6-6-6 6"/>`,

  'chevron-down': `
    <path d="m6 9 6 6 6-6"/>`,

  // One sheet behind another: the same document, more than once.
  copy: `
    <rect width="13" height="13" x="8" y="3" rx="2"/>
    <path d="M16 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h3"/>`,

  // A stack: sheets to be sorted one from another.
  layers: `
    <path d="m12 3 9 5-9 5-9-5 9-5z"/>
    <path d="m3 14 9 5 9-5"/>`,

  // Stepping through months in the calendar.
  'chevron-left': `
    <path d="m15 18-6-6 6-6"/>`,

  'chevron-right': `
    <path d="m9 18 6-6-6-6"/>`,

  x: `
    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>`,

  /*
   * The second batch: the marks the modals were using FontAwesome's solid set
   * for. Same rules as the first — few shapes, large, drawn for 14-18px —
   * so a dialogue reads as part of the same product as the table behind it.
   */

  // A password, hidden: the eye with a stroke through it.
  'eye-off': `
    <path d="M10.6 5.2A9.6 9.6 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-2.8 3.7"/>
    <path d="M6.3 7.3A17 17 0 0 0 2 12s3.6 7 10 7a9.8 9.8 0 0 0 4.6-1.1"/>
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/>
    <path d="m3 3 18 18"/>`,

  mail: `
    <rect width="18" height="14" x="3" y="5" rx="2"/>
    <path d="m3.5 6.5 8.5 6 8.5-6"/>`,

  lock: `
    <rect width="14" height="10" x="5" y="11" rx="2"/>
    <path d="M8 11V8a4 4 0 0 1 8 0v3"/>`,

  /* The three densities, each drawn as the rows it gives: two, three, four.
   *
   * The count is the whole message, so it climbs by one across the set. The
   * last is ruled inside a frame rather than drawn as loose bands — that is
   * what the tabular setting does to the list. */
  'density-spacious': `
    <rect width="18" height="7" x="3" y="3.5" rx="2"/>
    <rect width="18" height="7" x="3" y="13.5" rx="2"/>`,

  'density-compact': `
    <rect width="18" height="4.4" x="3" y="3.6" rx="1.2"/>
    <rect width="18" height="4.4" x="3" y="9.8" rx="1.2"/>
    <rect width="18" height="4.4" x="3" y="16" rx="1.2"/>`,

  'density-tabular': `
    <rect width="18" height="17" x="3" y="3.5" rx="1.5"/>
    <path d="M3 7.75h18"/><path d="M3 12h18"/><path d="M3 16.25h18"/>`,

  search: `
    <circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/>`,

  eye: `
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z"/>
    <circle cx="12" cy="12" r="3"/>`,

  // A warning: the triangle with the bar and the dot.
  'alert-triangle': `
    <path d="M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z"/>
    <path d="M12 9v4"/><path d="M12 17h.01"/>`,

  'alert-circle': `
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 8v4"/><path d="M12 16h.01"/>`,

  info: `
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 11v5"/><path d="M12 8h.01"/>`,

  'check-circle': `
    <circle cx="12" cy="12" r="9"/>
    <path d="m8.5 12.3 2.4 2.4 4.6-4.9"/>`,

  'x-circle': `
    <circle cx="12" cy="12" r="9"/>
    <path d="m15 9-6 6"/><path d="m9 9 6 6"/>`,

  // More than one person: two heads, the second half behind the first.
  users: `
    <path d="M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20"/>
    <circle cx="9.5" cy="7.5" r="3.5"/>
    <path d="M21 20v-1.5a4 4 0 0 0-3-3.9"/>
    <path d="M15.5 4.2a3.5 3.5 0 0 1 0 6.6"/>`,

  // A place on a map.
  'map-pin': `
    <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11z"/>
    <circle cx="12" cy="10" r="2.5"/>`,

  // A spreadsheet: the sheet with a grid in it.
  'file-spreadsheet': `
    <rect width="12" height="18" x="6" y="3" rx="2"/>
    <path d="M6 11h12"/><path d="M12 11v10"/>`,

  // Bringing a file in, and sending one out.
  'file-import': `
    <rect width="12" height="18" x="6" y="3" rx="2"/>
    <path d="M12 8v8"/><path d="m8.5 12.5 3.5 3.5 3.5-3.5"/>`,

  'file-check': `
    <rect width="12" height="18" x="6" y="3" rx="2"/>
    <path d="m9 12.5 2 2 4-4.5"/>`,

  'cloud-upload': `
    <path d="M7 18a4.5 4.5 0 0 1-.5-9 6 6 0 0 1 11.6 1.5A3.8 3.8 0 0 1 17.5 18"/>
    <path d="M12 21v-9"/><path d="m8.5 15.5 3.5-3.5 3.5 3.5"/>`,

  'arrow-right': `
    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>`,

  'arrow-up': `
    <path d="M12 19V5"/><path d="m5 12 7-7 7 7"/>`,

  // Sorting marks: the column's direction, as a single arrow.
  'arrow-up-narrow': `
    <path d="m6 9 4-4 4 4"/><path d="M10 5v14"/>
    <path d="M15 19h5"/><path d="M15 14h4"/><path d="M15 9h3"/>`,

  'arrow-down-narrow': `
    <path d="m6 15 4 4 4-4"/><path d="M10 19V5"/>
    <path d="M15 5h5"/><path d="M15 10h4"/><path d="M15 15h3"/>`,

  // A price tag, with its hole.
  tag: `
    <path d="M20.6 12.6 12.4 20.8a2 2 0 0 1-2.8 0l-6.4-6.4a2 2 0 0 1-.6-1.6l.5-6A2 2 0 0 1 5 5l6-.5a2 2 0 0 1 1.6.6l6.4 6.4a2 2 0 0 1 0 2.8z"/>
    <path d="M7.5 7.5h.01"/>`,

  hash: `
    <path d="M4 9h16"/><path d="M4 15h16"/>
    <path d="M10 3 8 21"/><path d="M16 3l-2 18"/>`,

  // A till receipt, torn off at the foot.
  receipt: `
    <path d="M5 21V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17l-3.5-2-3.5 2-3.5-2z"/>
    <path d="M9 8h6"/><path d="M9 12h6"/>`,

  clock: `
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 7v5.2l3.2 2"/>`,

  calendar: `
    <rect width="18" height="17" x="3" y="4" rx="2"/>
    <path d="M3 9h18"/><path d="M8 2v4"/><path d="M16 2v4"/>`,

  printer: `
    <path d="M7 8V3h10v5"/>
    <path d="M7 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/>
    <rect width="10" height="7" x="7" y="14" rx="1"/>`,

  send: `
    <path d="M21 3 3 10.5l7 3 3 7z"/><path d="M21 3 10 14"/>`,

  'external-link': `
    <path d="M14 4h6v6"/><path d="M20 4 11 13"/>
    <path d="M18 14v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5"/>`,

  // The handle for dragging a row.
  grip: `
    <circle cx="9" cy="6" r="1.2"/><circle cx="15" cy="6" r="1.2"/>
    <circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/>
    <circle cx="9" cy="18" r="1.2"/><circle cx="15" cy="18" r="1.2"/>`,

  database: `
    <ellipse cx="12" cy="6" rx="8" ry="3"/>
    <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6"/>
    <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3"/>`,

  star: `
    <path d="m12 3.5 2.7 5.5 6 .9-4.4 4.2 1.1 6-5.4-2.9-5.4 2.9 1.1-6L3.3 9.9l6-.9z"/>`,

  'shopping-cart': `
    <path d="M2 4h2.2l2.3 11.4a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L20 8H5"/>
    <circle cx="9" cy="20" r="1.3"/><circle cx="17" cy="20" r="1.3"/>`,

  banknote: `
    <rect width="20" height="12" x="2" y="6" rx="2"/>
    <circle cx="12" cy="12" r="2.5"/>
    <path d="M6 12h.01"/><path d="M18 12h.01"/>`,

  keyboard: `
    <rect width="20" height="14" x="2" y="5" rx="2"/>
    <path d="M6 9h.01"/><path d="M10 9h.01"/><path d="M14 9h.01"/><path d="M18 9h.01"/>
    <path d="M8 14h8"/>`,

  'zoom-in': `
    <circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/>
    <path d="M11 8v6"/><path d="M8 11h6"/>`,

  'zoom-out': `
    <circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/>
    <path d="M8 11h6"/>`,

  'check-double': `
    <path d="m2 13 4 4 7-8"/><path d="m11 15 2 2 9-10"/>`,

  /* A counterparty on file: the card their details are kept on.
   *
   * It was a figure with a tie — at 15px the tie is three strokes inside the
   * body and reads as a stain, and the figure itself is the same shape as
   * the person marks beside it. A card with a face on one side and two lines
   * of details on the other says "someone we have a record of", and shares
   * no outline with anything else in the set. */
  'user-tie': `
    <rect width="18" height="14" x="3" y="5" rx="2"/>
    <circle cx="8.8" cy="10.2" r="1.9"/>
    <path d="M5.9 15.5a3 3 0 0 1 5.8 0"/>
    <path d="M14.5 10h3.5"/><path d="M14.5 13.5h3.5"/>`,

  lightbulb: `
    <path d="M9 18h6"/><path d="M10 21h4"/>
    <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.3 1 2.1h5c0-.8.4-1.6 1-2.1A6 6 0 0 0 12 3z"/>`,

  crosshair: `
    <circle cx="12" cy="12" r="8"/>
    <path d="M12 2v4"/><path d="M12 18v4"/><path d="M2 12h4"/><path d="M18 12h4"/>`,

  'fuel-pump': `
    <path d="M4 21V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v16"/>
    <path d="M3 21h11"/><path d="M6.5 8h4"/>
    <path d="M13 10h3a2 2 0 0 1 2 2v4a1.5 1.5 0 0 0 3 0V9l-3-3"/>`,

  // A code printed on a product: bars of different weights.
  barcode: `
    <path d="M4 6v12"/><path d="M7.5 6v12"/><path d="M11 6v12"/>
    <path d="M14.5 6v12"/><path d="M17 6v12"/><path d="M20 6v12"/>`,

  // Working out a sum: the keys and the display.
  calculator: `
    <rect width="16" height="18" x="4" y="3" rx="2"/>
    <path d="M8 7h8"/><path d="M8.5 12h.01"/><path d="M12 12h.01"/><path d="M15.5 12h.01"/>
    <path d="M8.5 16h.01"/><path d="M12 16h.01"/><path d="M15.5 16h.01"/>`,

  // Settings of the technical kind: a spanner.
  wrench: `
    <path d="M15.5 3.5a5 5 0 0 0-5.9 6.4L3.3 16.2a2 2 0 0 0 0 2.8l1.7 1.7a2 2 0 0 0 2.8 0l6.3-6.3a5 5 0 0 0 6.4-5.9l-3 3-2.8-.7-.7-2.8z"/>`,

  // Stock held somewhere: a shed with its door.
  warehouse: `
    <path d="M3 21V9l9-5 9 5v12"/>
    <path d="M8 21v-7h8v7"/>`,

  puzzle: `
    <path d="M10 4a2 2 0 1 1 4 0v1h3a1 1 0 0 1 1 1v3h1a2 2 0 1 1 0 4h-1v3a1 1 0 0 1-1 1h-3v1a2 2 0 1 1-4 0v-1H7a1 1 0 0 1-1-1v-3H5a2 2 0 1 1 0-4h1V6a1 1 0 0 1 1-1h3z"/>`,

  check: `
    <path d="M4 12.5 9 17.5 20 6.5"/>`,

  /* Turning a page. An arrow bent three quarters of the way round, with its
     head on the side it turns towards — the direction has to be readable at
     13px, so the circle is open and the head is large. */
  'rotate-left': `
    <path d="M4 9V4"/><path d="M4 9h5"/>
    <path d="M4.2 9a8 8 0 1 1 1.3 7"/>`,

  'rotate-right': `
    <path d="M20 9V4"/><path d="M20 9h-5"/>
    <path d="M19.8 9a8 8 0 1 0-1.3 7"/>`,
};
</script>
